import { useSeo } from "@/lib/seo";
import { SCHOLAR_URL, TEAM } from "@/lib/constants";
import { Overline, Reveal, CTAButton, Section } from "@/components/shared";
import { PageHero } from "@/components/PageParts";
import { ArrowRight } from "lucide-react";

const different = [
  { t: "Scientific reasoning", d: "We reason from mechanisms, evidence and structure." },
  { t: "Mathematical modeling", d: "We make assumptions explicit and testable." },
  { t: "AI fluency", d: "We use AI where it accelerates well-defined work." },
  { t: "Implementation capacity", d: "We build systems, not just recommendations." },
  { t: "Systems-level diagnosis", d: "We locate leverage points across interacting parts." },
  { t: "Independent research orientation", d: "We investigate hard questions for their own sake." },
];

export default function About() {
  useSeo(
    "About Systems Beyond | Complex Systems Research & Consulting",
    "Meet the Systems Beyond team and learn how scientific modeling, AI, systems thinking and implementation experience shape our work."
  );

  return (
    <div>
      <PageHero
        overline="About"
        title="About Systems Beyond"
        subtitle="An independent research lab and consultancy connecting science, mathematics, computation, technology and real-world implementation."
      />

      <Section className="py-16 lg:py-24" grid>
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Overline>Why Systems Beyond exists</Overline>
          </div>
          <div className="lg:col-span-7">
            <p className="text-xl lg:text-2xl text-ink leading-snug tracking-tight">
              Systems Beyond was created to investigate and solve problems that require integrated
              thinking across science, mathematics, computation, technology and real-world
              implementation.
            </p>
          </div>
        </div>
      </Section>

      {/* Team */}
      <Section className="pb-16 lg:pb-24">
        <div className="grid lg:grid-cols-2 gap-6">
          <Reveal>
            <div className="border border-silver/50 bg-off-white h-full">
              <div className="overflow-hidden border-b border-silver/50 bg-charcoal">
                <img src={TEAM.david} alt="David Martinez, PhD — Founder and Head of Research" style={{ objectPosition: "center 20%" }} className="w-full h-96 object-cover grayscale hover:grayscale-0 transition-all duration-700" />
              </div>
              <div className="p-8 lg:p-10">
                <span className="font-mono-sci text-xs text-blue-gray">FOUNDER</span>
                <h3 className="mt-4 text-2xl lg:text-3xl text-ink">David Martinez, PhD</h3>
                <p className="text-sm text-dark-gray mt-1">Founder and Head of Research</p>
                <p className="mt-5 text-dark-gray leading-relaxed">
                  Complex systems scientist, experimental scientist and IT lead specializing in
                  predictive mathematical modeling, AI and data-driven platforms.
                </p>
                <a href={SCHOLAR_URL} target="_blank" rel="noopener noreferrer" data-testid="about-scholar-link" className="mt-7 inline-flex items-center gap-2 text-[13px] font-medium text-ink group">
                  View Google Scholar
                  <ArrowRight size={15} className="text-acid-dark group-hover:translate-x-1 transition-transform" />
                </a>
              </div>
            </div>
          </Reveal>
          <Reveal delay={120}>
            <div className="border border-silver/50 bg-off-white h-full">
              <div className="overflow-hidden border-b border-silver/50 bg-charcoal">
                <img src={TEAM.rodolfo} alt="Rodolfo Leo, EngPhys — Researcher and Technical Lead" style={{ objectPosition: "center 20%" }} className="w-full h-96 object-cover grayscale hover:grayscale-0 transition-all duration-700" />
              </div>
              <div className="p-8 lg:p-10">
                <span className="font-mono-sci text-xs text-blue-gray">TECHNICAL LEAD</span>
                <h3 className="mt-4 text-2xl lg:text-3xl text-ink">Rodolfo Leo, EngPhys</h3>
                <p className="text-sm text-dark-gray mt-1">Researcher and Technical Lead</p>
                <p className="mt-5 text-dark-gray leading-relaxed">
                  Physics engineer, IT specialist and project manager, ensuring that modeling and
                  strategy translate into robust technical implementation.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </Section>

      {/* What makes us different */}
      <Section dark className="py-20 lg:py-28">
        <div className="max-w-2xl">
          <Overline dark>What makes us different</Overline>
          <h2 className="mt-6 text-3xl lg:text-4xl tracking-tight leading-tight font-medium">
            Reasoning, rigor and the ability to build.
          </h2>
        </div>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
          {different.map((d, i) => (
            <Reveal key={d.t} delay={i * 80}>
              <div className="bg-charcoal p-8 h-full">
                <span className="w-2 h-2 bg-acid rounded-full inline-block" />
                <h3 className="mt-5 text-lg text-white">{d.t}</h3>
                <p className="mt-2 text-sm text-white/50 leading-relaxed">{d.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* Philosophy */}
      <Section className="py-20 lg:py-28" grid>
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Overline>Working philosophy</Overline>
          </div>
          <div className="lg:col-span-7">
            <p className="text-xl lg:text-2xl text-ink leading-snug tracking-tight">
              We do not believe complex problems are solved by isolated tools. We look for structure,
              interaction, feedback, constraints and leverage points. Then we design models,
              strategies and systems that can actually be used.
            </p>
            <CTAButton to="/contact" variant="accent" className="mt-10" testId="about-cta">Book a Consultation</CTAButton>
          </div>
        </div>
      </Section>
    </div>
  );
}
